import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEnhancedMessageTrackingData,
} from '../../services/outreach/api/outreachApiClient';
import type { EnhancedMessageTrackingData, MailgunEvent } from '../../services/outreach/types';
import { getCacheInvalidationManager } from '../../utils/cacheInvalidation';
import { useFallbackQuery, useCacheWarming } from '../useFallbackQuery';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

// Query key factory for tracking data
const TRACKING_QUERY_ROOT = 'tracking';
const TRACKING_QUERY_CAMPAIGNS_SEGMENT = 'campaigns';
const TRACKING_QUERY_PROVIDER_PREFIX = 'provider';

export type TrackingProviderKey = 'mailgun' | 'ses' | 'mixed' | 'unknown';

export const trackingKeys = {
  all: [TRACKING_QUERY_ROOT] as const,
  campaigns: () => [TRACKING_QUERY_ROOT, TRACKING_QUERY_CAMPAIGNS_SEGMENT] as const,
  campaign: (campaignId: string) => [TRACKING_QUERY_ROOT, TRACKING_QUERY_CAMPAIGNS_SEGMENT, campaignId] as const,
  campaignWithProvider: (campaignId: string, provider: TrackingProviderKey) =>
    [TRACKING_QUERY_ROOT, TRACKING_QUERY_CAMPAIGNS_SEGMENT, campaignId, `${TRACKING_QUERY_PROVIDER_PREFIX}:${provider}`] as const,
};

const EMAIL_PROVIDER_SESSION_KEY = 'outreach-email-provider';
const DEFAULT_STALE_TIME_MS = 30 * 1000;
const DEFAULT_REFETCH_INTERVAL_MS = 30 * 1000;
const SES_STALE_TIME_MS = 15 * 1000;
const SES_REFETCH_INTERVAL_MS = 15 * 1000;
const SES_MAX_CACHE_AGE_MS = 60 * 1000;

type ProviderDetectionCallback = (provider: TrackingProviderKey) => void;

const getStoredEmailProvider = (): TrackingProviderKey => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return 'unknown';
  }

  try {
    const stored = window.sessionStorage.getItem(EMAIL_PROVIDER_SESSION_KEY);
    if (stored === 'mailgun' || stored === 'ses') {
      return stored;
    }
    return 'unknown';
  } catch (error) {
    console.warn('Unable to read email provider from sessionStorage. Defaulting to unknown.', error);
    return 'unknown';
  }
};

const ensureIsoTimestamp = (value: unknown): string | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? undefined : value.toISOString();
  }

  if (typeof value === 'number') {
    const normalized = value > 1e12 ? value : value * 1000;
    const date = new Date(normalized);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  if (typeof value === 'string') {
    const numeric = Number(value);
    if (!Number.isNaN(numeric)) {
      return ensureIsoTimestamp(numeric);
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  }

  return undefined;
};

const mapSesEventTypeToStatus = (rawType?: string): EnhancedMessageTrackingData['status'] => {
  if (!rawType) {
    return 'pending';
  }

  const normalized = rawType.toLowerCase();
  switch (normalized) {
    case 'send':
    case 'sending':
    case 'sent':
      return 'sent';
    case 'delivery':
    case 'delivered':
      return 'delivered';
    case 'open':
    case 'opened':
      return 'opened';
    case 'click':
    case 'clicked':
      return 'clicked';
    case 'bounce':
    case 'bounced':
    case 'complaint':
    case 'complained':
    case 'reject':
    case 'rejected':
    case 'failure':
    case 'failed':
    case 'hardbounce':
    case 'softbounce':
      return 'failed';
    default:
      return 'pending';
  }
};

const isEnhancedMessageTrackingData = (value: unknown): value is EnhancedMessageTrackingData => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<EnhancedMessageTrackingData>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.recipient === 'string' &&
    typeof candidate.status === 'string' &&
    typeof candidate.timestamp === 'string' &&
    typeof candidate.mailgunMessageId === 'string'
  );
};

const isMailgunEvent = (value: unknown): value is MailgunEvent => {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as MailgunEvent & { event?: string };
  return typeof candidate.event === 'string';
};

const detectProviderFromEvent = (value: any): TrackingProviderKey => {
  if (!value || typeof value !== 'object') {
    return 'unknown';
  }

  const directProvider = typeof value.provider === 'string' ? value.provider.toLowerCase() : undefined;
  if (directProvider === 'mailgun' || directProvider === 'ses') {
    return directProvider;
  }

  if ('sesEventType' in value || 'sesEventDetail' in value || 'sesEventTimestamp' in value) {
    return 'ses';
  }

  if ('event' in value || 'mailgunMessageId' in value) {
    return 'mailgun';
  }

  if (Array.isArray(value.events)) {
    const nestedProviders = (value.events as unknown[])
      .map((nested) => detectProviderFromEvent(nested))
      .filter((nested): nested is TrackingProviderKey => nested !== 'unknown');

    if (nestedProviders.includes('ses')) {
      return 'ses';
    }

    if (nestedProviders.includes('mailgun')) {
      return 'mailgun';
    }
  }

  return 'unknown';
};

const transformMailgunEvent = (
  event: MailgunEvent & { subject?: string; messageId?: string; recipient?: string }
): EnhancedMessageTrackingData | null => {
  if (!event) {
    return null;
  }

  const statusMap: Record<MailgunEvent['event'], EnhancedMessageTrackingData['status']> = {
    accepted: 'sent',
    delivered: 'delivered',
    failed: 'failed',
    opened: 'opened',
    clicked: 'clicked',
    unsubscribed: 'pending',
    complained: 'failed',
  };

  const status = statusMap[event.event] ?? 'pending';
  const isoTimestamp =
    ensureIsoTimestamp(event.timestamp) ??
    ensureIsoTimestamp((event as unknown as { timestamp?: number | string }).timestamp) ??
    new Date().toISOString();

  const rawMessageId =
    event.message?.headers?.['message-id'] ||
    (event as unknown as { messageId?: string }).messageId ||
    '';

  const resolvedRecipient = event.recipient ?? (event as unknown as { recipient?: string }).recipient ?? 'unknown';
  const id = event.id || rawMessageId || `${resolvedRecipient}-${isoTimestamp}`;
  const resolvedMessageId = rawMessageId || id;

  const transformed: EnhancedMessageTrackingData = {
    id,
    recipient: resolvedRecipient,
    subject: (event as unknown as { subject?: string }).subject || `Campaign Email (${event.event})`,
    status,
    timestamp: isoTimestamp,
    mailgunMessageId: resolvedMessageId,
  };

  if (event.event === 'delivered') {
    transformed.deliveredAt = isoTimestamp;
  }

  if (event.event === 'opened') {
    transformed.openedAt = isoTimestamp;
  }

  if (event.event === 'clicked') {
    transformed.clickedAt = isoTimestamp;
  }

  if (event.event === 'failed') {
    transformed.bouncedAt = isoTimestamp;
  }

  if (event.event === 'complained') {
    transformed.complainedAt = isoTimestamp;
  }

  if (event.event === 'unsubscribed') {
    transformed.unsubscribedAt = isoTimestamp;
  }

  const deliveryStatus = event['delivery-status'];
  if (deliveryStatus?.message) {
    transformed.error = deliveryStatus.message;
  }

  if ((event as unknown as { reason?: string }).reason) {
    transformed.bounceReason = (event as unknown as { reason?: string }).reason;
  }

  const clientInfo = (event as unknown as { 'client-info'?: Record<string, string> })['client-info'];
  if (clientInfo) {
    transformed.clientInfo = {
      name: clientInfo['client-name'] ?? clientInfo['client_name'] ?? 'Unknown',
      os: clientInfo['client-os'] ?? clientInfo['client_os'] ?? 'Unknown',
      device: clientInfo['device-type'] ?? clientInfo['device_type'] ?? 'Unknown',
    };

    transformed.userAgent = clientInfo['user-agent'] ?? clientInfo['user_agent'];
  }

  const geolocation = (event as unknown as { geolocation?: { country?: string; region?: string; city?: string } }).geolocation;
  if (geolocation) {
    transformed.geolocation = {
      country: geolocation.country ?? '',
      region: geolocation.region ?? '',
      city: geolocation.city ?? '',
    };
  }

  return transformed;
};

const transformSesEvent = (event: Record<string, any>): EnhancedMessageTrackingData | null => {
  if (!event) {
    return null;
  }

  const timeline = Array.isArray(event.events) ? event.events : [];
  const normalizedTimeline = timeline
    .map((item: any) => {
      const rawType = (item?.type ?? item?.eventType ?? item?.event ?? item?.name) as string | undefined;
      const type = rawType ? rawType.toLowerCase() : undefined;
      const timestamp = ensureIsoTimestamp(item?.timestamp ?? item?.eventTimestamp ?? item?.sesEventTimestamp);

      if (!type || !timestamp) {
        return null;
      }

      return { type, timestamp, raw: item };
    })
    .filter((entry): entry is { type: string; timestamp: string; raw: any } => Boolean(entry));

  const findTimelineTimestamp = (...types: string[]) =>
    normalizedTimeline.find((entry) => types.includes(entry.type))?.timestamp;

  const baseTimestamp =
    ensureIsoTimestamp(event.sesEventTimestamp) ??
    ensureIsoTimestamp(event.timestamp) ??
    findTimelineTimestamp('send', 'sent') ??
    findTimelineTimestamp('delivery', 'delivered') ??
    normalizedTimeline[0]?.timestamp ??
    new Date().toISOString();

  const resolvedRecipient =
    event.recipient ??
    event.email ??
    event.sesEventDetail?.mail?.destination?.[0] ??
    normalizedTimeline.find((entry) => entry.raw?.recipient)?.raw?.recipient ??
    'unknown';

  const subject =
    event.subject ??
    event.sesEventDetail?.mail?.commonHeaders?.subject ??
    `Campaign Email (${event.sesEventType || 'ses'})`;

  const resolvedStatus = mapSesEventTypeToStatus(
    event.sesEventType ??
      event.status ??
      normalizedTimeline[normalizedTimeline.length - 1]?.type
  );

  const messageId =
    event.mailgunMessageId ??
    event.sesMessageId ??
    event.messageId ??
    event.id ??
    `${resolvedRecipient}-${baseTimestamp}`;

  const bounceDetail = event.sesEventDetail?.bounce;
  const complaintDetail = event.sesEventDetail?.complaint;
  const openDetail = event.sesEventDetail?.open;
  const clickDetail = event.sesEventDetail?.click;

  const enhanced: EnhancedMessageTrackingData = {
    id: event.id ?? messageId,
    recipient: resolvedRecipient,
    subject,
    status: resolvedStatus,
    timestamp: baseTimestamp,
    mailgunMessageId: messageId,
  };

  const deliveredAt = findTimelineTimestamp('delivery', 'delivered');
  if (deliveredAt) {
    enhanced.deliveredAt = deliveredAt;
  }

  const openedAt = findTimelineTimestamp('open', 'opened');
  if (openedAt) {
    enhanced.openedAt = openedAt;
  }

  const clickedAt = findTimelineTimestamp('click', 'clicked');
  if (clickedAt) {
    enhanced.clickedAt = clickedAt;
  }

  const bouncedAt = findTimelineTimestamp('bounce', 'bounced');
  if (bouncedAt) {
    enhanced.bouncedAt = bouncedAt;
  }

  const complainedAt = findTimelineTimestamp('complaint', 'complained');
  if (complainedAt) {
    enhanced.complainedAt = complainedAt;
  }

  const unsubscribedAt = findTimelineTimestamp('unsubscribe', 'unsubscribed');
  if (unsubscribedAt) {
    enhanced.unsubscribedAt = unsubscribedAt;
  }

  const errorMessage =
    bounceDetail?.diagnosticCode ??
    complaintDetail?.complaintFeedbackType ??
    event.error;

  if (errorMessage) {
    enhanced.error = errorMessage;
  }

  const bounceReasonParts = [
    bounceDetail?.bounceType,
    bounceDetail?.bounceSubType,
  ].filter(Boolean);

  if (bounceReasonParts.length > 0) {
    enhanced.bounceReason = bounceReasonParts.join(' / ');
  }

  const userAgent =
    clickDetail?.userAgent ??
    openDetail?.userAgent ??
    event.userAgent;

  if (userAgent) {
    enhanced.userAgent = userAgent;
  }

  return enhanced;
};

const normalizeProviderEvents = (
  events: unknown[],
  onProviderDetected?: ProviderDetectionCallback
): EnhancedMessageTrackingData[] => {
  if (!Array.isArray(events)) {
    onProviderDetected?.('unknown');
    return [];
  }

  const providers = new Set<TrackingProviderKey>();
  const normalized: EnhancedMessageTrackingData[] = [];

  for (const event of events) {
    const provider = detectProviderFromEvent(event);
    if (provider !== 'unknown') {
      providers.add(provider);
    }

    if (isEnhancedMessageTrackingData(event)) {
      normalized.push(event);
      continue;
    }

    if (provider === 'ses') {
      const transformed = transformSesEvent(event as Record<string, any>);
      if (transformed) {
        normalized.push(transformed);
      }
      continue;
    }

    if (provider === 'mailgun' && isMailgunEvent(event)) {
      const transformed = transformMailgunEvent(event);
      if (transformed) {
        normalized.push(transformed);
      }
      continue;
    }

    // Attempt SES transformation as a final fallback if the shape resembles SES data
    if (provider === 'unknown' && event && typeof event === 'object') {
      const sesCandidate = transformSesEvent(event as Record<string, any>);
      if (sesCandidate) {
        providers.add('ses');
        normalized.push(sesCandidate);
        continue;
      }

      if (isMailgunEvent(event)) {
        const mailgunCandidate = transformMailgunEvent(event);
        if (mailgunCandidate) {
          providers.add('mailgun');
          normalized.push(mailgunCandidate);
        }
      }
    }
  }

  const providerArray = Array.from(providers).filter((item) => item !== 'unknown');
  const summary: TrackingProviderKey =
    providerArray.length === 0
      ? 'unknown'
      : providerArray.length === 1
        ? providerArray[0]
        : 'mixed';

  onProviderDetected?.(summary);

  return normalized;
};

/**
 * Custom hook for fetching enhanced message tracking data with React Query
 * Now uses the secure Outreach API instead of direct Mailgun calls
 * Maintains the same interface for backward compatibility
 */
export const useTrackingData = (
  campaignId: string,
  options: {
    forceRefresh?: boolean;
    enableValidation?: boolean;
    maxCacheAge?: number;
    enableFallback?: boolean;
    priority?: 'high' | 'medium' | 'low';
  } = {}
) => {
  const {
    forceRefresh = false,
    enableValidation = true,
    maxCacheAge = 30 * 60 * 1000, // 30 minutes
    enableFallback = true,
    priority = 'medium',
  } = options;

  const { currentUser, loading: authLoading } = useAuth();
  const isAuthReady = !!currentUser && !authLoading;
  const canFetchTracking = !!campaignId && isAuthReady;

  const [providerKey, setProviderKey] = useState<TrackingProviderKey>(() => getStoredEmailProvider());
  const providerSummaryRef = useRef<TrackingProviderKey>(providerKey);

  const queryKeyWithProvider = useMemo(
    () => trackingKeys.campaignWithProvider(campaignId, providerKey),
    [campaignId, providerKey]
  );

  const fallbackQueryKey = useMemo(
    () => [...queryKeyWithProvider],
    [queryKeyWithProvider]
  );

  const isSesLikeProvider = providerKey === 'ses' || providerKey === 'mixed';
  const effectiveMaxCacheAge = isSesLikeProvider ? Math.min(maxCacheAge, SES_MAX_CACHE_AGE_MS) : maxCacheAge;
  const effectiveStaleTime = isSesLikeProvider ? SES_STALE_TIME_MS : DEFAULT_STALE_TIME_MS;
  const effectiveRefetchInterval = isSesLikeProvider ? SES_REFETCH_INTERVAL_MS : DEFAULT_REFETCH_INTERVAL_MS;
  const effectiveGcTime = Math.max(effectiveMaxCacheAge * 2, effectiveStaleTime * 2);

  const updateProviderSummary = useCallback<ProviderDetectionCallback>(
    (detected) => {
      providerSummaryRef.current = detected;
    },
    []
  );

  const syncProviderState = useCallback(() => {
    const detected = providerSummaryRef.current;
    if (detected !== 'unknown' && detected !== providerKey) {
      setProviderKey(detected);
    }
  }, [providerKey]);

  const fetchTrackingRecords = useCallback(async (): Promise<EnhancedMessageTrackingData[]> => {
    if (!campaignId || campaignId === 'default' || campaignId.length < 10) {
      console.log(`Skipping tracking fetch for invalid/new campaign ID: ${campaignId}`);
      updateProviderSummary('unknown');
      return [];
    }

    console.log(`Fetching tracking data via Outreach API for campaign: ${campaignId}`);

    const result = await getEnhancedMessageTrackingData(campaignId, {
      forceRefresh,
      enableValidation,
      maxCacheAge: effectiveMaxCacheAge,
    });

    const rawEvents = Array.isArray(result.data) ? result.data : [];
    const normalizedRecords = normalizeProviderEvents(rawEvents, updateProviderSummary);

    console.log(
      `Normalized ${normalizedRecords.length} tracking records (raw count: ${rawEvents.length}) for campaign: ${campaignId}`
    );

    syncProviderState();

    return normalizedRecords;
  }, [campaignId, forceRefresh, enableValidation, effectiveMaxCacheAge, updateProviderSummary, syncProviderState]);

  // Always call both hooks, but conditionally use their results
  const fallbackQuery = useFallbackQuery(
    fallbackQueryKey,
    {
      fetcher: fetchTrackingRecords,
      enabled: canFetchTracking && enableFallback,
      maxCacheAge: effectiveMaxCacheAge,
      priority,
      endpoint: '/api/tracking',
      enablePrefetch: priority === 'high',
      fallbackValue: [],
    }
  );

  const standardQuery = useQuery({
    queryKey: queryKeyWithProvider,
    queryFn: fetchTrackingRecords,
    enabled: canFetchTracking && !enableFallback,
    staleTime: effectiveStaleTime,
    gcTime: effectiveGcTime,
    refetchInterval: effectiveRefetchInterval,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as any).status;
        if (status >= 400 && status < 500) {
          return false;
        }
      }
      return failureCount < 2;
    },
  });

  // Return the appropriate query result based on enableFallback
  return enableFallback ? fallbackQuery : standardQuery;
};

/**
 * Hook to manually invalidate tracking data cache
 * Useful for forcing a refresh after sending emails or other mutations
 * Now integrated with the cache invalidation manager
 */
export const useInvalidateTrackingData = () => {
  const queryClient = useQueryClient();
  
  return {
    // Invalidate all tracking data
    invalidateAll: () => {
      try {
        const manager = getCacheInvalidationManager();
        manager.invalidateByEvent('manual_refresh');
      } catch {
        // Fallback to direct query client invalidation
        queryClient.invalidateQueries({ queryKey: trackingKeys.all });
      }
    },
    
    // Invalidate specific campaign tracking data
    invalidateCampaign: (campaignId: string) => {
      try {
        const manager = getCacheInvalidationManager();
        manager.invalidateCampaign(campaignId, false); // Don't cascade to avoid double invalidation
      } catch {
        // Fallback to direct query client invalidation
        queryClient.invalidateQueries({ queryKey: trackingKeys.campaign(campaignId) });
      }
    },
    
    // Remove specific campaign from cache
    removeCampaign: (campaignId: string) => 
      queryClient.removeQueries({ queryKey: trackingKeys.campaign(campaignId) }),

    // Invalidate after email sent
    invalidateOnEmailSent: (campaignId: string) => {
      try {
        const manager = getCacheInvalidationManager();
        manager.invalidateByEvent('email_sent', campaignId);
      } catch {
        // Fallback
        queryClient.invalidateQueries({ queryKey: trackingKeys.campaign(campaignId) });
      }
    },

    // Invalidate after campaign update
    invalidateOnCampaignUpdate: (campaignId: string) => {
      try {
        const manager = getCacheInvalidationManager();
        manager.invalidateByEvent('campaign_updated', campaignId);
      } catch {
        // Fallback
        queryClient.invalidateQueries({ queryKey: trackingKeys.campaign(campaignId) });
      }
    },
  };
};

/**
 * Hook to prefetch tracking data for better UX
 * Useful for preloading data when user hovers over campaign links
 * Now uses Outreach API client
 */
export const usePrefetchTrackingData = () => {
  const queryClient = useQueryClient();
  
  return (campaignId: string) => {
    try {
      const manager = getCacheInvalidationManager();
      manager.prefetchCampaignData(campaignId);
    } catch {
      // Fallback to direct prefetch using Outreach API with provider-aware keys
      const provider = getStoredEmailProvider();
      queryClient.prefetchQuery({
        queryKey: trackingKeys.campaignWithProvider(campaignId, provider),
        queryFn: async () => {
          const result = await getEnhancedMessageTrackingData(campaignId);
          const rawEvents = Array.isArray(result.data) ? result.data : [];
          return normalizeProviderEvents(rawEvents);
        },
        staleTime: provider === 'ses' ? SES_STALE_TIME_MS : DEFAULT_STALE_TIME_MS,
      });
    }
  };
};

/**
 * Hook to get cached tracking data without triggering a fetch
 * Useful for accessing already loaded data in other components
 */
export const useCachedTrackingData = (campaignId: string): EnhancedMessageTrackingData[] | undefined => {
  const queryClient = useQueryClient();
  const providersToCheck: TrackingProviderKey[] = [];
  const storedProvider = getStoredEmailProvider();

  if (storedProvider !== 'unknown') {
    providersToCheck.push(storedProvider);
  }

  // Add fallback providers to inspect without duplicating values
  for (const candidate of ['ses', 'mailgun', 'mixed', 'unknown'] as TrackingProviderKey[]) {
    if (!providersToCheck.includes(candidate)) {
      providersToCheck.push(candidate);
    }
  }

  for (const provider of providersToCheck) {
    const cached = queryClient.getQueryData<EnhancedMessageTrackingData[]>(
      trackingKeys.campaignWithProvider(campaignId, provider)
    );

    if (Array.isArray(cached)) {
      return cached;
    }
  }

  const legacyCache = queryClient.getQueryData<EnhancedMessageTrackingData[]>(trackingKeys.campaign(campaignId));
  return Array.isArray(legacyCache) ? legacyCache : undefined;
};

/**
 * Hook to get merge statistics for a campaign
 * Note: This functionality may be limited with the new API
 */
export const useCampaignMergeStats = (campaignId: string) => {
  return useCallback(() => {
    if (!campaignId) return null;
    
    // This function may need to be updated based on what the Outreach API provides
    console.warn('getCampaignMergeStatistics may not be available with Outreach API');
    return null;
  }, [campaignId]);
};

/**
 * Hook to update tracking data with new events incrementally
 * Note: This functionality may be handled differently with the new API
 */
export const useUpdateTrackingData = () => {
  const queryClient = useQueryClient();
  
  return useCallback(async (
    campaignId: string, 
    newEvents: any[], 
    options: { enableValidation?: boolean } = {}
  ) => {
    try {
      console.warn('updateTrackingDataWithNewEvents may not be available with Outreach API');
      
      // For now, just invalidate the cache to trigger a fresh fetch
      queryClient.invalidateQueries({ queryKey: trackingKeys.campaign(campaignId) });
      
      // Trigger cache invalidation manager update
      try {
        const cacheManager = getCacheInvalidationManager();
        cacheManager.invalidateByEvent('data_merged', campaignId);
      } catch (error) {
        console.warn('Failed to notify cache invalidation manager:', error);
      }
      
      return {
        data: [],
        metadata: {
          note: 'Event merging handled by backend API'
        }
      };
    } catch (error) {
      console.error('Failed to update tracking data:', error);
      throw error;
    }
  }, [queryClient]);
};

/**
 * Hook to warm cache with critical tracking data
 * Useful for preloading important campaigns on app startup
 * Now uses Outreach API client
 */
export const useWarmTrackingCache = () => {
  const { warmCache, isWarming } = useCacheWarming();
  
  const warmCriticalCampaigns = useCallback(async (campaignIds: string[]) => {
    const criticalQueries = campaignIds.map(campaignId => ({
      queryKey: [...trackingKeys.campaign(campaignId)], // Convert readonly to mutable
      fetcher: async () => {
        const result = await getEnhancedMessageTrackingData(campaignId);
        return result.data;
      },
      priority: 'high' as const,
    }));
    
    await warmCache(criticalQueries);
  }, [warmCache]);
  
  return { warmCriticalCampaigns, isWarming };
};
